import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import { useAuth } from '../shared/contexts/auth-context';
import { PERMISSIONS, isMasterUser } from '../shared/utils/permissions';
import PermissionWrapper from '../components/PermissionWrapper';
import ClientCheckForm from '../components/tfs/ClientCheckForm';
import SearchHistory from '../components/tfs/SearchHistory';
import UploadTfsList from '../components/tfs/UploadTfsList';

const GENERATE_PDF = gql`
  mutation GeneratePDF($id: ID!) {
    generatePDF(id: $id)
  }
`;

const Fica = () => {
  const [activeTab, setActiveTab] = useState('check');
  const [searchResult, setSearchResult] = useState(null);
  const [generatePDF, { loading: pdfLoading }] = useMutation(GENERATE_PDF);
  const { user } = useAuth();

  const handleSearchResult = (result) => {
    setSearchResult(result);
  };

  const handleDownloadPDF = async (id) => {
    try {
      const response = await generatePDF({
        variables: { id }
      });
      const pdfUrl = response.data.generatePDF;
      window.open(`${window.location.origin}${pdfUrl}`, '_blank');
    } catch (error) {
      console.error('Download PDF Error:', error);
      alert('Failed to download PDF: ' + error.message);
    }
  };

  const canUpload = isMasterUser(user) || user?.user?.role === 'superuser';
  
  const tabs = [
    { id: 'check', label: 'Check Client', icon: '🔍' },
    { id: 'history', label: 'Search History', icon: '📋' },
    ...(canUpload ? [{ id: 'upload', label: 'Upload TFS List', icon: '📤' }] : [])
  ];

  return (
    <PermissionWrapper permission={PERMISSIONS.FICA}>
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">FICA - TFS Check System</h1>
          <p className="mt-2 text-sm text-gray-600">
            Check clients against the TFS list and perform background verification
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-8">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="bg-white">
          {activeTab === 'check' && (
            <div className="p-6">
              <ClientCheckForm 
                onSearchResult={handleSearchResult}
                onDownloadPDF={handleDownloadPDF}
              />
            </div>
          )}

          {activeTab === 'history' && (
            <div className="p-6">
              <SearchHistory 
                onDownloadPDF={handleDownloadPDF}
                userRole={user?.role}
              />
            </div>
          )}

          {activeTab === 'upload' && canUpload && (
            <div className="p-6">
              <UploadTfsList />
            </div>
          )}
        </div>

        {/* Information Section */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-blue-900 mb-2">About FICA TFS Check</h3>
          <div className="text-sm text-blue-800 space-y-2">
            <p>
              <strong>TFS Service</strong> checks help verify client legitimacy by:
            </p>
            <ul className="list-disc list-inside ml-4 space-y-1">
              <li>Searching against the official TFS database</li>
              <li>Performing Google searches for additional verification</li>
              <li>Generating detailed PDF reports</li>
              <li>Maintaining search history for compliance</li>
            </ul>
            <p className="mt-4">
              <strong>Note:</strong> This system is for compliance verification purposes only. 
              All searches are logged and can be reviewed by authorized personnel.
            </p>
          </div>
        </div>
        </div>
      </div>
    </PermissionWrapper>
  );
};

export default Fica;